/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { dateUtils } from '@opengeoweb/shared';
import type {
  WarningEvent,
  WarningEventBody,
} from '../../../store/warningsForm/types';
import {
  WarningEventMode,
  WarningEventType,
} from '../../../store/warningsForm/types';
import { WarningLevel } from '../../PublicWarningsForm/LevelField';

const DEFAULT_WARNING_DURATION_HOURS = 24;
const WARNING_START_OFFSET_HOURS = 3;
const WARNING_START_OFFSET_DAYS = 7;

export const warningEventTypes: {
  translationKey: string;
  key: WarningEventType;
  defaultDayRange: [number, number];
}[] = [
  {
    translationKey: 'public-warning-event-type-early-warning',
    key: WarningEventType.EARLY_WARNING,
    defaultDayRange: [2, 7],
  },
  {
    translationKey: 'public-warning-event-type-warning',
    key: WarningEventType.WARNING,
    defaultDayRange: [0, 1],
  },
];

export const warningEventModes: {
  translationKey: string;
  key: WarningEventMode;
}[] = [
  {
    translationKey: 'public-warning-event-mode-actual',
    key: WarningEventMode.ACTUAL,
  },
  {
    translationKey: 'public-warning-event-mode-test',
    key: WarningEventMode.TEST,
  },
];

export const getDefaultDayRangeForType = (
  type: WarningEventType,
): [number, number] =>
  warningEventTypes.find((warningType) => warningType.key === type)
    ?.defaultDayRange ?? [0, 1];

export const getDefaultWarningTimes = (
  type: WarningEventType,
  now: Date = dateUtils.getNowUtc(),
): { validFrom: string; validUntil: string } => {
  // Whole minutes only, no milliseconds, to avoid issues with the backend when comparing times
  const startOfMinute = dateUtils.set(now, { seconds: 0, milliseconds: 0 });
  const validFrom =
    type === WarningEventType.EARLY_WARNING
      ? dateUtils.add(dateUtils.startOfDayUTC(startOfMinute), {
          days: WARNING_START_OFFSET_DAYS,
        })
      : dateUtils.add(startOfMinute, { hours: WARNING_START_OFFSET_HOURS });
  const validUntil = dateUtils.add(validFrom, {
    hours: DEFAULT_WARNING_DURATION_HOURS,
  });
  return {
    validFrom: validFrom.toISOString(),
    validUntil: validUntil.toISOString(),
  };
};

export const defaultValues: WarningEvent = {
  incident: '',
  phenomenon: '',
  validFrom: '',
  validUntil: '',
  warningEventType: WarningEventType.EARLY_WARNING,
  mode: WarningEventMode.ACTUAL,
  // one warning by default will be added by the form (not here to keep the date instantiation in sync)
  warnings: [],
  texts: {
    headlineOriginal: '',
    headlineTranslation: '',
    conciseSummaryOriginal: '',
    conciseSummaryTranslation: '',
    descriptionOriginal: '',
    descriptionTranslation: '',
  },
};

export const warningEventItemDefaultValues: WarningEventBody = {
  incident: '',
  validFrom: '',
  validUntil: '',
  level: '',
  probability: '' as unknown as number,
  domain: '',
  warningProposalSource: '',
  authority: '',
  phenomenon: '',
  type: 0,
  warning_handle_uuid: '',
  areas: [],
};

export const buildDefaultWarningItem = (
  event: Pick<WarningEvent, 'warningEventType' | 'incident' | 'phenomenon'>,
): WarningEventBody => {
  const { validFrom, validUntil } = getDefaultWarningTimes(
    event.warningEventType,
  );
  return {
    ...warningEventItemDefaultValues,
    level: WarningLevel.moderate,
    validFrom,
    validUntil,
    incident: event.incident,
    phenomenon: event.phenomenon,
  };
};

/**
 * Copy of an existing warning, stripped of all identity/status fields so it is
 * treated as a brand new, unsaved warning. All content (times, level,
 * probability, areas) is copied over as is.
 */
export const buildDuplicateWarningItem = (
  source: WarningEventBody,
): WarningEventBody => {
  const rest = { ...source };
  delete rest.id;
  delete rest.linked_to_id;
  delete rest.proposal_id;
  delete rest.status;

  if (rest.areas) {
    rest.areas = rest.areas.map((area) => {
      const areaCopy = { ...area };
      delete areaCopy.published_warning_uuid;
      return areaCopy;
    });
  }

  return {
    ...rest,
    warning_handle_uuid: '',
  };
};

export const getDefaultValues = (
  initialValues?: Partial<WarningEvent>,
): WarningEvent => {
  const values = { ...defaultValues, ...initialValues };
  return {
    ...values,
    warnings:
      values.warnings.length > 0
        ? values.warnings
        : [buildDefaultWarningItem(values)],
  };
};
